import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'diagnostics_theme';

export type Theme = 'light' | 'dark';

/** User override: null = follow system preference; otherwise use this theme. */
export type ThemeOverride = Theme | null;

type ThemeContextValue = {
  /** Current effective theme (used for data-theme and UI). */
  theme: Theme;
  /** User override if set; null when following system. */
  override: ThemeOverride;
  /** Set override. Pass null to follow system again. */
  setOverride: (value: ThemeOverride) => void;
  /** Toggle between light and dark (sets override, does not clear to system). */
  setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredOverride(): ThemeOverride {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // ignore
  }
  return null;
}

function getSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [override, setOverrideState] = useState<ThemeOverride>(readStoredOverride);
  const [systemTheme, setSystemTheme] = useState<Theme>(getSystemTheme);

  const theme = useMemo<Theme>(
    () => (override !== null ? override : systemTheme),
    [override, systemTheme]
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setSystemTheme(getSystemTheme());
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  const setOverride = useCallback((value: ThemeOverride) => {
    setOverrideState(value);
    try {
      if (value === null) {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, value);
      }
    } catch {
      // ignore
    }
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setOverride(t);
  }, [setOverride]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, override, setOverride, setTheme }),
    [theme, override, setOverride, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
